import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { type EntryId } from '../components/shared-components/DocumentToReactRender/DocumentToReactRender'
import { type ContentfulPage } from '../hooks/useGraphQL'
import { getPageIdViaPath, getPagePathViaId } from '../utils/getPagePath'

interface NavigationContextProps {
  findFullUrlFromId: (id: string) => string | null
  findPageIdFromPath: (id: string) => string | null
  setFullPagePaths: (pages: ContentfulPage[]) => void
}

const NavigationContext = createContext<NavigationContextProps | null>(null)

export const useNavigation = () => {
  const context = useContext(NavigationContext)

  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }

  return context
}

export type NavBar = {
  loginText: string
  loginLinks: {
    id: EntryId
    externalUrl: string
    mainText: string
  }[]
}

export const NavigationProvider = ({
  children,
  fullPaths,
}: {
  children: ReactNode
  fullPaths: ContentfulPage[]
}) => {
  const [fullPagePaths, setFullPagePaths] =
    useState<ContentfulPage[]>(fullPaths)

  const findFullUrlFromId = useCallback(
    (id: string): string | null => {
      return getPagePathViaId(id, fullPagePaths)
    },
    [fullPagePaths],
  )

  const findPageIdFromPath = useCallback(
    (path: string): string | null => {
      return getPageIdViaPath(path, fullPagePaths)
    },
    [fullPagePaths],
  )

  useEffect(() => {
    setFullPagePaths(fullPaths)
  }, [fullPaths])

  const value = {
    findFullUrlFromId,
    findPageIdFromPath,
    setFullPagePaths,
  }

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  )
}
